import { VscJson } from "react-icons/vsc"
import { LuWrench } from "react-icons/lu"
import { IconChevronRight } from "@tabler/icons-react"
import { type DataTableColumn } from "@/components/ui/data-table"
import { ProviderIcon } from "@/components/provider-pill"
import { Chip } from "@/components/Chip"
import { ListTooltip, type ListTooltipItem } from "@/components/ListTooltip"
import { formatTimestamp } from "@/lib/utils"
import { formatCurrency } from "@/lib/utils"
import type { LogEntry } from "@/data/types/costExplorer.types"
import { LuGroup } from 'react-icons/lu'


export const logsTableColumns: DataTableColumn<LogEntry>[] = [
  {
    id: "timestamp",
    header: "Timestamp",
    accessorKey: "timestamp",
    flex: 1.2,
    enableSorting: true,
    cell: ({ row }) => (
      <span className="text-[#08090A]">
        {formatTimestamp(row.original.timestamp)}
      </span>
    ),
  },
  {
    id: "provider",
    header: "Provider",
    accessorKey: "provider",
    flex: 0.8,
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <ProviderIcon provider={row.original.provider} className="w-4 h-4" />
        <span className="text-[#08090A]">{row.original.provider}</span>
      </div>
    ),
  },
  {
    id: "model",
    header: "Model",
    accessorKey: "model",
    flex: 1.2,
    enableSorting: true,
    cell: ({ row }) => (
      <code className="rounded bg-[#F7F8F8] border border-[#EDEEF3] px-[6px] py-[2px] text-[11px] font-mono text-[#08090A]">
        {row.original.model}
      </code>
    ),
  },
  {
    id: "modality",
    header: "Modality",
    accessorKey: "modality",
    flex: 0.7,
    enableSorting: false,
    cell: ({ row }) => (
      <Chip
        label={row.original.modality.charAt(0).toUpperCase() + row.original.modality.slice(1).toLowerCase()}
        icon={<LuGroup className="size-3 text-[#654EDA]" />}
      />
    ),
  },
  {
    id: "cost",
    header: "Cost",
    accessorKey: "cost",
    flex: 0.9,
    align: "left",
    enableSorting: true,
    cell: ({ row }) => (
      <span className="text-[#08090A] font-mono text-[12px]">
        {formatCurrency(row.original.cost, 6, 6)}
      </span>
    ),
  },
  {
    id: "usage",
    header: "Usage",
    accessorKey: "usage",
    flex: 0.8,
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[#08090A] text-[13px]">
        {row.original.usage}
      </span>
    ),
  },
  {
    id: "tools",
    header: "Tools",
    flex: 0.7,
    enableSorting: false,
    cell: ({ row }) => {
      const tools = row.original.tools
      const entries = Object.entries(tools)

      if (entries.length === 0) {
        return <span className="text-[#8A8F98] text-[13px]">—</span>
      }

      // Convert tools entries to tooltip items
      const tooltipItems: ListTooltipItem[] = entries.map(([key, value]) => ({
        id: key,
        label: key.replace(/_/g, ' '),
        sublabel: value,
      }))

      return (
        <ListTooltip
          items={tooltipItems}
          title="Tools Used"
          emptyMessage="No tools"
          side="bottom"
          align="start"
        >
          <div className="cursor-default">
            <Chip
              label={`${entries.length} ${entries.length === 1 ? 'tool' : 'tools'}`}
              icon={<LuWrench className="size-3 text-[#654EDA]" />}
            />
          </div>
        </ListTooltip>
      )
    },
  },
  {
    id: "metadata",
    header: "Metadata",
    flex: 0.7,
    enableSorting: false,
    cell: ({ row }) => {
      const metadata = row.original.metadata
      const entries = Object.entries(metadata)

      // Convert metadata entries to tooltip items
      const tooltipItems: ListTooltipItem[] = entries.map(([key, value]) => ({
        id: key,
        label: key,
        sublabel: value,
      }))

      return (
        <ListTooltip
          items={tooltipItems}
          title="Metadata"
          emptyMessage="No metadata"
          side="bottom"
          align="start"
        >
          <div className="cursor-default">
            <Chip
              label={`${entries.length} ${entries.length === 1 ? 'field' : 'fields'}`}
              icon={<VscJson className="size-3 text-[#654EDA]" />}
            />
          </div>
        </ListTooltip>
      )
    },
  },
  {
    id: "actions",
    header: "",
    flex: 0.3,
    enableSorting: false,
    cell: () => (
      <div className="flex items-center justify-end">
        <IconChevronRight className="size-4 text-[#8A8F98]" />
      </div>
    ),
  },
]